import { InputLabel, SelectProps } from '@mui/material';
import { forwardRef } from 'react';

import { DropdownMenuItem } from './components/DropdownMenuItem';
import { getDropdownItemDisplayValue } from './helpers';
import { useDropdownLocators } from './hooks/useDropdownLocators';
import { StyledFormControl } from './styledComponents/StyledFormControl';
import { StyledPlaceholder } from './styledComponents/StyledPlaceholder';
import { StyledSelect } from './styledComponents/StyledSelect';
import { ViewModeContainer, ViewModeLabel, ViewModeValue } from './styledComponents/ViewMode';
import type { DropdownListItem } from './types';
import { InputComponentWidth } from '../../types';
import { getWidth } from '../../utils/getWidth';

export type DropdownProps = {
  /**
   * A unique identifier for the dropdown component.
   * Used to distinguish this dropdown from others in the application.
   */
  id: string;

  /**
   * Specifies the width of the dropdown.
   * Can accept predefined sizes like 'xxlarge', 'xlarge', etc., or a custom number value.
   */
  width?: InputComponentWidth;

  /**
   * Placeholder text to display when no value is selected.
   */
  placeholder?: string;

  /**
   * A list of options available for selection in the dropdown.
   * Each option should conform to the `DropdownListItem` type.
   */
  options?: DropdownListItem[];

  /**
   * If true, the dropdown is displayed in a read-only view mode.
   * In this mode, the value is shown but cannot be changed.
   */
  viewMode?: boolean;
} & Omit<SelectProps, 'notched'>;

export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>((props, ref) => {
  const {
    id,
    label,
    required = false,
    width = 'medium',
    size = 'medium',
    value = '',
    placeholder = 'Select value from list',
    disabled = false,
    readOnly,
    options,
    viewMode = false,
    ...rest
  } = props;

  const {
    formControlNodeId,
    viewModeContainerNodeId,
    labelNodeId,
    ariaDropdownLabel,
    ariaDropdownSelect,
  } = useDropdownLocators({ key: id });

  const getSelectedValue = (value: string) => {
    if (!value) {
      return <StyledPlaceholder disabled={disabled}>{placeholder}</StyledPlaceholder>;
    }
    const selectedOption = options?.find((item) => item.value === value);
    return selectedOption ? getDropdownItemDisplayValue(selectedOption) : '';
  };

  const displayLabel = viewMode ? label : `${label}${required ? ' *' : ''}`;

  return (
    <StyledFormControl
      id={formControlNodeId}
      variant="outlined"
      sx={{ m: 0, width: getWidth(width) }}
      aria-labelledby={ariaDropdownLabel}
    >
      {viewMode ? (
        <ViewModeContainer id={viewModeContainerNodeId}>
          <ViewModeLabel aria-label={ariaDropdownLabel}>{displayLabel}</ViewModeLabel>
          <ViewModeValue aria-labelledby={ariaDropdownLabel}>
            {getSelectedValue(value as string)}
          </ViewModeValue>
        </ViewModeContainer>
      ) : (
        <>
          <InputLabel
            id={labelNodeId}
            shrink
            sx={{ color: disabled ? '#d2d4d7' : '#333333' }}
            disabled={disabled}
            aria-label={ariaDropdownLabel}
          >
            {displayLabel}
          </InputLabel>
          <StyledSelect
            {...rest}
            ref={ref}
            labelId={labelNodeId}
            aria-labelledby={ariaDropdownLabel}
            label={label}
            displayEmpty
            notched
            size={size}
            disabled={disabled}
            renderValue={(value) => getSelectedValue(value as string)}
            value={value}
            inputProps={{
              readOnly,
              'aria-readonly': readOnly,
            }}
            aria-label={ariaDropdownSelect}
            placeholder={placeholder}
          >
            {options?.map((item, index) => (
              <DropdownMenuItem
                id={id}
                key={item.divider ? `divider-${index}` : `${item.value}`}
                option={item}
                value={item.value as string}
                index={index}
              />
            ))}
          </StyledSelect>
        </>
      )}
    </StyledFormControl>
  );
});

export default Dropdown;
